import type { QueryClient } from '@tanstack/react-query'
import { LOTUS_LOBBY_GROUPS } from '../constants/lotusCasinoLobbyGroups'
import { LOTUS_TOP_PROVIDERS } from '../constants/lotusCasinoTopProviders'
import { getLotusTabByIndex } from '../constants/lotusCasinoTabs'
import {
  casinoTypesForProvider,
  dedupeProviderTypePairs,
  fetchCasinoProviderList,
  LOTUS_CASINO_STALE_MS,
  lotusCasinoKeys,
  type CasinoProviderTypePair,
} from './lotusCasinoQueries'

function pairsForProvider(provider: string): CasinoProviderTypePair[] {
  return casinoTypesForProvider(provider).map((type) => ({ provider, type }))
}

/** Core provider pools used across lobby rows and header tabs. */
export function getLotusCasinoCorePrefetchPairs(): CasinoProviderTypePair[] {
  const providers = new Set<string>()

  LOTUS_TOP_PROVIDERS.forEach((p) => providers.add(p.categoryId))
  LOTUS_LOBBY_GROUPS.forEach((group) => {
    group.providerCategories?.forEach((p) => providers.add(p))
  })

  const pairs: CasinoProviderTypePair[] = []
  providers.forEach((provider) => {
    pairs.push(...pairsForProvider(provider))
  })

  return dedupeProviderTypePairs(pairs)
}

export function getLotusCasinoTabPrefetchPairs(tabIndex: number): CasinoProviderTypePair[] {
  const tab = getLotusTabByIndex(tabIndex)
  const lobbyGroup = LOTUS_LOBBY_GROUPS.find((g) => g.tabIndex === tabIndex)
  const providers = new Set<string>()

  if (lobbyGroup?.providerCategories?.length) {
    lobbyGroup.providerCategories.forEach((p) => providers.add(p))
  } else if (lobbyGroup?.useTopProviders) {
    LOTUS_TOP_PROVIDERS.forEach((p) => providers.add(p.categoryId))
  } else if (tab?.targetType === 'category' && tab.target) {
    providers.add(tab.target)
  }

  const pairs: CasinoProviderTypePair[] = []
  providers.forEach((provider) => {
    pairs.push(...pairsForProvider(provider))
  })

  return dedupeProviderTypePairs(pairs)
}

export function prefetchCasinoProviderPairs(
  queryClient: QueryClient,
  pairs: CasinoProviderTypePair[],
  preLoginMode: boolean,
): void {
  pairs.forEach(({ provider, type }) => {
    void queryClient.prefetchQuery({
      queryKey: lotusCasinoKeys.providerList(provider, type, preLoginMode),
      queryFn: () => fetchCasinoProviderList(provider, type, preLoginMode),
      staleTime: LOTUS_CASINO_STALE_MS,
    })
  })
}

export function prefetchLotusCasinoCore(
  queryClient: QueryClient,
  preLoginMode: boolean,
): void {
  prefetchCasinoProviderPairs(queryClient, getLotusCasinoCorePrefetchPairs(), preLoginMode)
}

export function prefetchLotusCasinoTab(
  queryClient: QueryClient,
  tabIndex: number,
  preLoginMode: boolean,
): void {
  prefetchCasinoProviderPairs(
    queryClient,
    getLotusCasinoTabPrefetchPairs(tabIndex),
    preLoginMode,
  )
}
